import React, { useState, useEffect, ChangeEvent } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import HoldButton from '../../Shared/HoldButton';
import { loadBattlePrefs, saveBattlePrefs } from '../../../utils/battlePreferences';
import styles from './LevelUp.module.css';

interface PrimaryPoints {
  health: number;
  strength: number;
  endurance: number;
  initiative: number;
  dodge: number;
  axe: number;
  sword: number;
  mace: number;
  staff: number;
  shield: number;
  dagger: number;
  chain: number;
}

interface SecondaryPoints {
  learning: number;
  luck: number;
  discipline: number;
  leadership: number;
  provocation: number;
}

interface MercenaryData extends PrimaryPoints, SecondaryPoints {
  id: number;
  name: string;
  level: number;
  primarySkillPoints: number;
  secondarySkillPoints: number;
  maxHealth: number;
  effectiveMaxHealth?: number;
}

interface RaceBonuses {
  health: number;
  strength: number;
  endurance: number;
  initiative: number;
  dodge: number;
  learning: number;
  luck: number;
  discipline: number;
  axe: number;
  sword: number;
  mace: number;
  staff: number;
  shield: number;
  dagger: number;
  chain: number;
}

const SKILL_DISPLAY: Record<string, string> = {
  health: 'Health', strength: 'Strength', endurance: 'Endurance', initiative: 'Initiative', dodge: 'Dodge',
  axe: 'Axe', sword: 'Sword', mace: 'Mace', staff: 'Staff', shield: 'Shield', dagger: 'Dagger', chain: 'Chain',
  learning: 'Learning', luck: 'Luck', discipline: 'Discipline', leadership: 'Leadership', provocation: 'Provocation',
};

const SKILL_DESCRIPTIONS: Record<string, string> = {
  health: 'Increases your Hit Points (HP) and overall durability in combat. Your HP is capped at 3x your total Strength - investing more Health than that is wasted until you also raise Strength.',
  strength: 'Increases physical damage dealt and carrying capacity, reducing penalties from heavy equipment. Also raises your HP cap - maximum HP cannot exceed 3x your total Strength.',
  endurance: 'Improves resilience and stamina, allowing you to fight for more rounds before exhaustion.',
  initiative: 'Determines turn order in combat. Crucial for heavy weapon users to strike before the enemy.',
  dodge: 'Increases your chance to completely evade incoming attacks.',
  axe: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  sword: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  mace: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  staff: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  shield: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  dagger: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  chain: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  learning: 'Increases experience gained from battles and other activities.',
  luck: 'Increases chances of winning in gambling minigames and finding better loot.',
  discipline: 'Increases the maximum number of daily rounds your mercenary can perform.',
  leadership: 'Provides a stat bonus to all party members in team battles (only applies from the leader with the highest skill).',
  provocation: 'Increases the likelihood of being targeted by enemies, protecting weaker allies.',
};

const PRIMARY_SKILL_GROUPS = [
  { label: 'Attributes',    keys: ['health', 'strength', 'endurance', 'initiative', 'dodge'] as const },
  { label: 'Weapon Skills', keys: ['axe', 'sword', 'mace', 'staff', 'shield', 'dagger', 'chain'] as const },
] as const;

const SECONDARY_KEYS = ['learning', 'luck', 'discipline', 'leadership', 'provocation'] as const;

const LevelUp: React.FC = () => {
  const navigate = useNavigate();
  const { mercenary, setMercenary } = useMercenary();
  const [userMercenary, setUserMercenary] = useState<MercenaryData | null>(null);
  const [allocatedPrimary, setAllocatedPrimary] = useState<PrimaryPoints>({
    health: 0, strength: 0, endurance: 0, initiative: 0, dodge: 0,
    axe: 0, sword: 0, mace: 0, staff: 0, shield: 0, dagger: 0, chain: 0,
  });
  const [allocatedSecondary, setAllocatedSecondary] = useState<SecondaryPoints>({
    learning: 0, luck: 0, discipline: 0, leadership: 0, provocation: 0,
  });
  const [primaryLeft, setPrimaryLeft]     = useState<number>(mercenary?.primarySkillPoints || 0);
  const [secondaryLeft, setSecondaryLeft] = useState<number>(mercenary?.secondarySkillPoints || 0);
  const [raceBonuses, setRaceBonuses]     = useState<RaceBonuses | null>(null);
  const [toastMessage, setToastMessage]   = useState<string | null>(null);
  const [toastType, setToastType]         = useState<'success' | 'error'>('success');
  const [loading, setLoading]             = useState<boolean>(true);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message); setToastType(type);
  };

  // Close the active stat tooltip when tapping/clicking anywhere else
  useEffect(() => {
    if (!activeTooltip) return;
    const close = () => setActiveTooltip(null);
    const id = setTimeout(() => {
      document.addEventListener('click', close, { once: true });
      document.addEventListener('touchstart', close, { once: true });
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('click', close);
      document.removeEventListener('touchstart', close);
    };
  }, [activeTooltip]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get('/api/auth/user', { withCredentials: true });
      const user = response.data;
      const storedId = localStorage.getItem('mercenaryId');
      const selectedId = storedId ? parseInt(storedId, 10) : null;
      const mercenaryData: MercenaryData = user.mercenaries.find((g: MercenaryData) => g.id === selectedId) || user.mercenaries[0];
      if (mercenaryData) {
        setUserMercenary(mercenaryData);
        setPrimaryLeft(mercenaryData.primarySkillPoints);
        setSecondaryLeft(mercenaryData.secondarySkillPoints);
        // Do NOT call setMercenary here - auth/user returns raw (unscaled) stat values
        // which would overwrite the correctly race-scaled values in the global context.
        localStorage.setItem('mercenaryId', mercenaryData.id.toString());
      }
      setLoading(false);
    } catch {
      displayToast('Failed to fetch mercenary data.', 'error');
      setLoading(false);
    }
  };

  const fetchRaceBonuses = async () => {
    try {
      const response = await axios.get(`/api/mercenary/race-bonus?race=${mercenary?.race}`);
      const b = response.data[0];
      setRaceBonuses({
        health: b.health, strength: b.strength, endurance: b.endurance, initiative: b.initiative, dodge: b.avoidance,
        learning: b.learningCapacity, luck: b.luck, discipline: b.discipline,
        axe: b.weaponAxes, sword: b.weaponSwords, mace: b.weaponMaces, staff: b.weaponStaves,
        shield: b.weaponShields, dagger: b.weaponDaggers, chain: b.weaponChain,
      });
    } catch {
      displayToast('Failed to fetch race bonuses.', 'error');
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchRaceBonuses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrimaryChange = (skill: keyof PrimaryPoints, rawValue: string) => {
    const value = rawValue === '' ? 0 : Math.max(0, parseInt(rawValue, 10) || 0);
    const newPts = { ...allocatedPrimary, [skill]: value };
    const total  = Object.values(newPts).reduce((a, b) => a + b, 0);
    if (total <= userMercenary!.primarySkillPoints) {
      setAllocatedPrimary(newPts);
      setPrimaryLeft(userMercenary!.primarySkillPoints - total);
    }
  };

  const handleSecondaryChange = (skill: keyof SecondaryPoints, rawValue: string) => {
    const value = rawValue === '' ? 0 : Math.max(0, parseInt(rawValue, 10) || 0);
    const newPts = { ...allocatedSecondary, [skill]: value };
    const total  = Object.values(newPts).reduce((a, b) => a + b, 0);
    if (total <= userMercenary!.secondarySkillPoints) {
      setAllocatedSecondary(newPts);
      setSecondaryLeft(userMercenary!.secondarySkillPoints - total);
    }
  };

  const handleSubmit = async () => {
    if (primaryLeft !== 0 || secondaryLeft !== 0) {
      displayToast('All points must be allocated before confirming.', 'error');
      return;
    }
    try {
      const response = await axios.post(
        '/api/mercenary/level-up',
        {
          primarySkillPoints: Object.values(allocatedPrimary).reduce((a, b) => a + b, 0),
          secondarySkillPoints: Object.values(allocatedSecondary).reduce((a, b) => a + b, 0),
          attributes: { ...allocatedPrimary, ...allocatedSecondary },
          mercenaryId: userMercenary!.id,
        },
        { withCredentials: true }
      );
      const updated = response.data;
      setMercenary((prev: any) => prev ? { ...prev, ...updated } : updated);
      localStorage.setItem('mercenary', JSON.stringify(updated));

      // Bump saved level filters by +1 to account for the new level
      const id = userMercenary!.id;

      const duelPrefs = loadBattlePrefs(id, 'duel', { maxLevel: userMercenary!.level + 2 });
      saveBattlePrefs(id, 'duel', { ...duelPrefs, maxLevel: (duelPrefs.maxLevel as number) + 1 });

      const defaultIncomingMin = Math.max(1, userMercenary!.level - 5);
      const defaultIncomingMax = userMercenary!.level + 5;
      const incomingPrefs = loadBattlePrefs(id, 'contract-incoming', {
        showReady: false,
        minLevel: defaultIncomingMin,
        maxLevel: defaultIncomingMax,
        strategy: 'Normal',
        surrenderHp: 0.5,
      });
      saveBattlePrefs(id, 'contract-incoming', {
        ...incomingPrefs,
        minLevel: Math.max(1, (incomingPrefs.minLevel as number) + 1),
        maxLevel: (incomingPrefs.maxLevel as number) + 1,
      });

      displayToast('Advancement confirmed! Your mercenary grows stronger.', 'success');
      navigate('/mercenary');
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to save skill points.', 'error');
    }
  };

  const getBaseValue = (skill: string): number => {
    if (!userMercenary) return 0;
    return skill === 'health'
      ? userMercenary.maxHealth
      : Math.floor((userMercenary as any)[skill] as number) || 0;
  };

  const applyRaceBonus = (skill: string, baseValue: number): number => {
    const multiplier =
      skill === 'leadership' || skill === 'provocation'
        ? 1.0
        : raceBonuses?.[skill as keyof RaceBonuses] || 1;
    return Math.floor(baseValue * multiplier);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading mercenary data…</p>
      </div>
    );
  }

  if (!userMercenary) {
    return <div className={styles.errorState}>No mercenary found. Please refresh the page.</div>;
  }

  const totalPrimaryPoints   = userMercenary.primarySkillPoints;
  const totalSecondaryPoints = userMercenary.secondarySkillPoints;
  const primaryUsedPct   = totalPrimaryPoints > 0 ? Math.round(((totalPrimaryPoints - primaryLeft) / totalPrimaryPoints) * 100) : 0;
  const secondaryUsedPct = totalSecondaryPoints > 0 ? Math.round(((totalSecondaryPoints - secondaryLeft) / totalSecondaryPoints) * 100) : 0;

  const renderSkillRow = (
    skill: string,
    allocated: number,
    onChangeFn: (v: string) => void,
    onDec: () => void,
    onInc: () => void,
    disableInc: boolean
  ) => {
    const current  = getBaseValue(skill);
    const newTotal = current + allocated;
    const withRace = skill === 'health'
      ? Math.floor(newTotal * (raceBonuses?.health ?? 1))
      : applyRaceBonus(skill, newTotal);
    const raceDiff = withRace - newTotal;
    const raceSign = raceDiff > 0 ? '+' : '';
    const multiplier =
      skill === 'leadership' || skill === 'provocation'
        ? 1.0
        : skill === 'health'
          ? (raceBonuses?.health ?? 1)
          : (raceBonuses?.[skill as keyof RaceBonuses] ?? 1);
    const racePct = Math.round((multiplier - 1) * 100);
    const racePctStr = racePct > 0 ? `+${racePct}%` : racePct < 0 ? `${racePct}%` : null;

    return (
      <div key={skill} className={styles.skillRow}>
        <span className={styles.skillName} style={{ display: 'flex', alignItems: 'center' }}>
          {SKILL_DISPLAY[skill]}
          <span
            className={styles.skillInfoBtn}
            role="button"
            tabIndex={0}
            aria-label={`Info about ${SKILL_DISPLAY[skill]}`}
            onClick={(e) => { e.stopPropagation(); setActiveTooltip(prev => prev === skill ? null : skill); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveTooltip(prev => prev === skill ? null : skill); }}
          >
            ?
            {activeTooltip === skill && (
              <span className={styles.skillTooltipPopup}>
                {SKILL_DESCRIPTIONS[skill]}
              </span>
            )}
          </span>
        </span>

        <span className={styles.skillCurrent}>{current}</span>

        <HoldButton type="button" className={styles.stepperBtn} onClick={onDec} disabled={allocated === 0}>−</HoldButton>
        <input
          type="number"
          className={styles.skillInput}
          value={allocated === 0 ? '' : allocated}
          placeholder="0"
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeFn(e.target.value)}
          min="0"
        />
        <HoldButton type="button" className={styles.stepperBtn} onClick={onInc} disabled={disableInc}>+</HoldButton>

        <span className={`${styles.skillNewTotal} ${allocated > 0 ? styles.skillNewTotalHighlight : ''}`}>
          {newTotal}
        </span>

        <span className={styles.skillWithRace}>
          {racePctStr && (
            <span className={`${styles.racePct} ${racePct > 0 ? styles.raceDiffPos : styles.raceDiffNeg}`}>
              {racePctStr}&nbsp;
            </span>
          )}
          {withRace}
          {raceDiff !== 0 && (
            <span className={`${styles.raceDiff} ${raceDiff > 0 ? styles.raceDiffPos : styles.raceDiffNeg}`}>
              &nbsp;({raceSign}{raceDiff})
            </span>
          )}
          {skill === 'health' && (raceBonuses?.health ?? 1) !== 1}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.levelUpContainer}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={4000} onClose={() => setToastMessage(null)} />
      )}

      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Advancement</h1>
          <p className={styles.pageSubtitle}>{userMercenary.name} - Level {userMercenary.level}</p>
        </div>
        <div className={styles.headerStats}>
          <div className={styles.statPill}>
            <span className={styles.statPillLabel}>Primary</span>
            <span className={`${styles.statPillValue} ${primaryLeft === 0 ? styles.statPillDone : ''}`}>
              {primaryLeft}/{totalPrimaryPoints}
            </span>
          </div>
          <div className={styles.statPill}>
            <span className={styles.statPillLabel}>Secondary</span>
            <span className={`${styles.statPillValue} ${secondaryLeft === 0 ? styles.statPillDone : ''}`}>
              {secondaryLeft}/{totalSecondaryPoints}
            </span>
          </div>
        </div>
      </div>

      {/* Points progress bars */}
      <div className={styles.progressPanel}>
        <div className={styles.progressRow}>
          <span className={styles.progressLabel}>Primary Points Spent</span>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${primaryUsedPct}%` }} />
          </div>
          <span className={styles.progressPct}>{primaryUsedPct}%</span>
        </div>
        <div className={styles.progressRow}>
          <span className={styles.progressLabel}>Secondary Points Spent</span>
          <div className={styles.progressBar}>
            <div className={styles.progressFillSecondary} style={{ width: `${secondaryUsedPct}%` }} />
          </div>
          <span className={styles.progressPct}>{secondaryUsedPct}%</span>
        </div>
      </div>

      {/* ── Primary Skill Groups ── */}
      {PRIMARY_SKILL_GROUPS.map((group) => (
        <div key={group.label} className={styles.skillBlock}>
          <h2 className={styles.groupTitle}>{group.label}</h2>
          <div className={styles.skillTableHeader}>
            <span>Skill</span>
            <span className={styles.colCenter}>Current</span>
            <span className={styles.addPointsHeader}>Add Points</span>
            <span className={styles.colCenter}>New Total</span>
            <span className={styles.raceColHeader}>
              With Racial<span className={styles.raceColNote}>(multiplier applied)</span>
            </span>
          </div>
          <div className={styles.skillTableBody}>
            {group.keys.map((skill) =>
              renderSkillRow(
                skill,
                allocatedPrimary[skill],
                (v) => handlePrimaryChange(skill, v),
                () => handlePrimaryChange(skill, String(Math.max(0, allocatedPrimary[skill] - 1))),
                () => handlePrimaryChange(skill, String(allocatedPrimary[skill] + 1)),
                primaryLeft === 0
              )
            )}
          </div>
        </div>
      ))}

      {/* ── Secondary Skills ── */}
      <div className={styles.skillBlock}>
        <h2 className={styles.groupTitle}>Secondary Skills</h2>
        <div className={styles.skillTableHeader}>
          <span>Skill</span>
          <span className={styles.colCenter}>Current</span>
          <span className={styles.addPointsHeader}>Add Points</span>
          <span className={styles.colCenter}>New Total</span>
          <span className={styles.raceColHeader}>
            With Racial<span className={styles.raceColNote}>(multiplier applied)</span>
          </span>
        </div>
        <div className={styles.skillTableBody}>
          {SECONDARY_KEYS.map((skill) =>
            renderSkillRow(
              skill,
              allocatedSecondary[skill],
              (v) => handleSecondaryChange(skill, v),
              () => handleSecondaryChange(skill, String(Math.max(0, allocatedSecondary[skill] - 1))),
              () => handleSecondaryChange(skill, String(allocatedSecondary[skill] + 1)),
              secondaryLeft === 0
            )
          )}
        </div>
      </div>

      {/* ── Confirm button ── */}
      <div className={styles.confirmRow}>
        {(primaryLeft > 0 || secondaryLeft > 0) && (
          <p className={styles.warningText}>
            ⚠ You still have unspent points - all points must be allocated to confirm.
          </p>
        )}
        <button
          onClick={handleSubmit}
          className={`${styles.confirmButton} ${primaryLeft === 0 && secondaryLeft === 0 ? styles.confirmButtonReady : ''}`}
        >
          ✦ Confirm Advancement
        </button>
      </div>
    </div>
  );
};

export default LevelUp;
